import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { Sparkle } from "../controls";
import { formatShortDate } from "../chiefComplaints/dateHelpers";
import AnthropometricService from "./AnthropometricService";
import TrendModal from "./TrendModal";
import { PARAMETER_META, interpretationBand } from "./data";
import { ANTH_V2_REFRESH, ANTH_V2_SELECT_PARAM } from "./events";
// Anthro is a projection over api-lab-investigation-v2 (ADR-0001 Phase 4).
// A Weight/Height/Waist/Hip/Blood-group write from the Lab-investigations
// section, or from any other surface that writes to LI directly, must
// refresh this column too — otherwise the two views can diverge.
import { LI_V2_REFRESH } from "../labInvestigations/events";

/**
 * Anthropometric — the content column. Backed by the CC-style grouped
 * response: `doc.parameters[]` has ≤ 7 entries (one per parameter), each with
 * a cached `current` and an append-only `statusHistory[]`. Rows are rendered
 * in the fixed PARAMETER_META order; parameters not yet recorded show `—`.
 *
 * The trend modal opens from the Analysis column and reads the parameter's
 * `statusHistory` directly — no filter/sort dance on the client.
 */

const findParameter = (parameters, key) => {
  for (const p of parameters || []) {
    if (p.parameter === key) return p;
  }
  return null;
};

const Anthropometric = ({ patient, capturedItems }) => {
  const patientId = patient && (patient.id || patient.patientID);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trendTarget, setTrendTarget] = useState(null); // { meta, paramEntry }

  const load = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    const svc = new AnthropometricService();
    svc.list(
      patientId,
      (res) => {
        setDoc(res || { parameters: [], version: 0 });
        setLoading(false);
      },
      (err) => {
        setError((err && err.message) || "Failed to load anthropometric data");
        setLoading(false);
      }
    );
  }, [patientId]);

  useEffect(() => {
    load();
    EventService.addListener(ANTH_V2_REFRESH, load);
    EventService.addListener(LI_V2_REFRESH, load);
    return () => {
      EventService.removeListener(ANTH_V2_REFRESH, load);
      EventService.removeListener(LI_V2_REFRESH, load);
    };
  }, [load]);

  const parameters = (doc && doc.parameters) || [];

  // Transcript-mode overlay: fixed-7 table stays the same shape (no
  // staged rows — BMI + WHR are server-derived; user-entered parameters
  // update in place), but a row with a pending captured value gets a
  // peach highlight + sparkle so the reviewer can spot which
  // measurement has a proposed change waiting on the right column.
  const capturedByParam = useMemo(() => {
    const map = {};
    if (!capturedItems) return map;
    for (const it of capturedItems) {
      if (!it) continue;
      if (it.state !== "added_in_call" && it.state !== "edited_in_call") continue;
      const key = it.payload && it.payload.parameter;
      if (typeof key === "string" && key) map[key] = it;
    }
    return map;
  }, [capturedItems]);

  const rows = useMemo(
    () =>
      PARAMETER_META.map((meta) => {
        const entry = findParameter(parameters, meta.key);
        const captured = capturedByParam[meta.key];
        return {
          meta,
          entry,
          _captured: captured || null,
          _capturedIsAi: captured ? (captured.confidence || 0) > 0 : false,
        };
      }),
    [parameters, capturedByParam]
  );

  const selectParam = (key) => {
    EventService.emit(ANTH_V2_SELECT_PARAM, key);
  };

  const openTrend = (meta, entry) => {
    if (!entry || !entry.statusHistory || entry.statusHistory.length === 0) return;
    setTrendTarget({ meta, paramEntry: entry });
  };

  if (!patientId) {
    return <div className="ph-slot-empty"><p>Waiting for patient…</p></div>;
  }
  if (loading && !doc) {
    return <div className="ph-slot-empty"><p>Loading anthropometric data…</p></div>;
  }
  if (error) {
    return (
      <div className="ph-slot-empty">
        <p>{error}</p>
        <button type="button" className="sec-btn sec-btn-ghost" onClick={load}>Retry</button>
      </div>
    );
  }

  return (
    <div className="an3-card">
      <header className="an3-head">
        <h2 className="an3-title">Anthropometric data</h2>
        <span className="an3-count">&middot; {PARAMETER_META.length} parameters</span>
      </header>

      <div className="an3-table" role="table">
        <div className="an3-thead" role="row">
          <span role="columnheader">Parameter</span>
          <span role="columnheader">Value</span>
          <span role="columnheader">Unit</span>
          <span role="columnheader">Reads as</span>
          <span role="columnheader">Reported by</span>
          <span role="columnheader">Last recorded</span>
          <span role="columnheader">Analysis</span>
        </div>

        {rows.map(({ meta, entry, _captured, _capturedIsAi }) => {
          const current = entry ? entry.current : null;
          const hasValue = !!current;
          const interpretation = hasValue ? current.interpretation : "unknown";
          const band = interpretationBand(interpretation);
          const isAuto = meta.auto;
          const showPill = hasValue && interpretation !== "unknown";
          const canView = meta.viewable && hasValue;

          return (
            <div
              className={`an3-row${showPill ? " is-tall" : ""}${_captured ? " is-captured" : ""}`}
              role="row"
              key={meta.key}
            >
              <span className="an3-cell an3-c-param" role="cell">
                {isAuto ? (
                  <span className="an3-param">
                    {meta.label}
                    {/* Auto tag (3015:5056): the value is derived, not entered. */}
                    <span className="an3-auto-tag">Auto</span>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="an3-param-btn"
                    onClick={() => selectParam(meta.key)}
                    title={`Edit ${meta.label}`}
                  >
                    {_capturedIsAi ? <span className="cc-spark" aria-label="AI captured" title="AI captured"><Sparkle aria-hidden /></span> : null}
                    {meta.label}
                  </button>
                )}
              </span>
              <span className="an3-cell an3-c-val" role="cell">
                {hasValue && current.valueDisplay ? (
                  // Strip the trailing unit so the Value column reads
                  // "13.58" instead of "13.58 kg" — the Unit column
                  // already surfaces it, and repeating it in both cells
                  // makes numeric scanning harder. Leaves special-format
                  // displays (height "1'1"", blood group "B+") untouched
                  // because they don't end with the plain-text unit.
                  current.unit && current.valueDisplay.endsWith(` ${current.unit}`)
                    ? current.valueDisplay.slice(0, -(current.unit.length + 1))
                    : current.valueDisplay
                ) : (
                  <span className="an3-dash">-</span>
                )}
              </span>
              <span className="an3-cell an3-c-unit" role="cell">
                {/* "None" is the sentinel unit the Anthro→LI adapter
                    writes for unitless parameters (Blood group, and
                    the WHR ratio). Render it as the same dash placeholder
                    an empty unit gets — otherwise the table shows the
                    literal word "None". */}
                {hasValue
                  && current.unit
                  && String(current.unit).trim().toLowerCase() !== "none"
                  ? current.unit
                  : <span className="an3-dash">-</span>}
              </span>
              <span className="an3-cell" role="cell">
                {showPill ? (
                  <span className="an3-pill" style={{ background: band.bg, color: band.fg }}>
                    {band.label}
                  </span>
                ) : (
                  <span className="an3-dash">-</span>
                )}
              </span>
              {/* Auto rows read "Auto" in the SOURCE column (3015:5067) no matter
                  what name the backend cached — the derivation IS the source. */}
              <span
                className={`an3-cell an3-c-source${isAuto ? " is-auto" : ""}`}
                role="cell"
              >
                {isAuto ? (
                  "Auto"
                ) : hasValue && current.reportedByName ? (
                  current.reportedByName
                ) : (
                  <span className="an3-dash">-</span>
                )}
              </span>
              <span className="an3-cell an3-c-date" role="cell">
                {hasValue ? formatShortDate(current.testDate) : <span className="an3-dash">-</span>}
              </span>
              <span className="an3-cell" role="cell">
                {canView ? (
                  <button
                    type="button"
                    className="an3-trend"
                    onClick={() => openTrend(meta, entry)}
                    title={`View the ${meta.label} trend`}
                  >
                    View trend
                  </button>
                ) : (
                  <span className="an3-dash">-</span>
                )}
              </span>
            </div>
          );
        })}
      </div>

      {trendTarget && (
        <TrendModal
          meta={trendTarget.meta}
          paramEntry={trendTarget.paramEntry}
          onClose={() => setTrendTarget(null)}
        />
      )}
    </div>
  );
};

Anthropometric.propTypes = {
  patient: PropTypes.object,
  capturedItems: PropTypes.array,
};

export default Anthropometric;
